import { useEffect } from 'react'
import { ClerkProvider, useAuth } from '@clerk/clerk-react'
import { RouterProvider } from '@tanstack/react-router'
import { Toaster } from 'sonner'
import { setAuthTokenProvider } from '@/api/client'
import { TooltipProvider } from '@/components/ui/Tooltip'
import { QueryProvider } from './providers/QueryProvider'
import { ThemeProvider } from './providers/ThemeProvider'
import { router } from './router'
import { useMediaSession } from './useMediaSession'

const CLERK_PUBLISHABLE_KEY: string = (import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined) ?? ''
if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error(
    'VITE_CLERK_PUBLISHABLE_KEY is not set. Add it to frontend/.env.local — see .env.example.',
  )
}

function RouterWithAuth() {
  const auth = useAuth()
  const { getToken, isLoaded, isSignedIn, userId } = auth
  // Wire Clerk's token getter into the API client so every request goes out
  // with a Bearer header. `getToken` is bound to the Clerk singleton so this
  // effect runs once, but the deps array is correct in case Clerk ever
  // returns a fresh reference.
  useEffect(() => {
    setAuthTokenProvider(getToken)
  }, [getToken])
  // The cookie <img>/<video> use to read the caller's files (card #277).
  const mediaReady = useMediaSession(isLoaded, isSignedIn, userId)
  // TanStack Router only re-runs `beforeLoad` on navigation, not when the
  // injected context changes. Without this invalidate, signing out leaves
  // the user staring at the protected page they were on (state cleared,
  // but the route component still mounted) until the next link click.
  //
  // Guard: only invalidate once the RouterProvider is mounted — after Clerk
  // has loaded AND the media cookie wait is over. Before that we return null
  // below, so the router's internal context is still the placeholder
  // `{ auth: undefined! }` and beforeLoad would NPE on
  // `context.auth.isSignedIn`.
  useEffect(() => {
    if (!isLoaded || !mediaReady) return
    void router.invalidate()
  }, [isSignedIn, isLoaded, mediaReady])
  // Defer rendering routes until Clerk has hydrated session from cookie.
  // Without this, beforeLoad guards see `isSignedIn: undefined` and bounce
  // signed-in users to /login on first paint.
  if (!isLoaded || !mediaReady) return null
  return <RouterProvider router={router} context={{ auth }} />
}

export function App() {
  return (
    <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} telemetry={false}>
      <ThemeProvider>
        <QueryProvider>
          <TooltipProvider delayDuration={200}>
            <RouterWithAuth />
            {/* Sonner's own palette: "dark" to match the app — "system" would
                follow the OS and paint light toasts on the dark UI. Positioned
                top-right to leave the main action button (bottom-right) untouched. */}
            <Toaster theme="dark" position="top-right" richColors closeButton />
          </TooltipProvider>
        </QueryProvider>
      </ThemeProvider>
    </ClerkProvider>
  )
}
